import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { getCardgames } from '../services/supabaseService';

interface CardImageProps {
  src?: string;
  alt?: string;
  className?: string;
  game?: string | number;
  gameId?: string | number;
  cardBackUrl?: string;
  layoutId?: string;
  objectFit?: 'cover' | 'contain';
}

const DEFAULT_CARD_BACK = 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=400';

// Global cache for card back URLs to avoid redundant queries across dozens of cards
const cardBackCache = new Map<string, string>();
let pendingGamesPromise: Promise<any[]> | null = null;

// Populate initial known defaults for instant zero-latency render
const KNOWN_GAME_BACKS: Record<string, string> = {
  'pokemon': 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=400',
  'magic': 'https://cards.scryfall.io/card_backs/large.jpg',
  'yugioh': 'https://images.ygoprodeck.com/images/cards/back.jpg'
};
Object.entries(KNOWN_GAME_BACKS).forEach(([k, v]) => cardBackCache.set(k, v));

const getCacheKey = (gameId?: string | number, game?: string | number): string => {
  if (gameId) return `id:${gameId}`;
  if (game) return `game:${String(game).toLowerCase().trim()}`;
  return '';
};

// Helper to extract image URL from string, object or array
const extractImageUrl = (val: any): string => {
  if (!val) return '';
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      return extractImageUrl(val[0]);
    }
    return extractImageUrl(val.large || val.medium || val.small || val.url || val.src || val.png || val.normal || '');
  }
  return String(val).trim();
};

const CardImageComponent: React.FC<CardImageProps> = ({
  src,
  alt = 'Card Game Card',
  className = '',
  game,
  gameId,
  cardBackUrl,
  layoutId,
  objectFit
}) => {
  const cacheKey = getCacheKey(gameId, game);
  const initialBack = cardBackUrl || (cacheKey && cardBackCache.get(cacheKey)) || DEFAULT_CARD_BACK;

  const [resolvedBack, setResolvedBack] = useState<string>(initialBack);
  const [isFrontLoading, setIsFrontLoading] = useState(true);
  const [hasFrontError, setHasFrontError] = useState(false);
  const frontImgRef = useRef<HTMLImageElement>(null);

  // Normalize image URL string
  const rawSrc = extractImageUrl(src);
  const isForbiddenSrc = typeof rawSrc === 'string' && rawSrc.includes('dstcynss47vun.cloudfront.net');
  const normalizedSrc = isForbiddenSrc ? '' : (rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc);

  // Check fit mode
  const fitMode = objectFit || (className.includes('object-contain') ? 'contain' : 'cover');

  // Resolve card back URL with deduped global query
  useEffect(() => {
    if (cardBackUrl) {
      setResolvedBack(cardBackUrl);
      if (cacheKey) cardBackCache.set(cacheKey, cardBackUrl);
      return;
    }

    if (cacheKey && cardBackCache.has(cacheKey)) {
      setResolvedBack(cardBackCache.get(cacheKey)!);
      return;
    }

    let isMounted = true;

    if (!pendingGamesPromise) {
      pendingGamesPromise = getCardgames().catch(() => []);
    }

    pendingGamesPromise.then((gamesList) => {
      if (!gamesList || !Array.isArray(gamesList)) {
        if (isMounted) setResolvedBack(DEFAULT_CARD_BACK);
        return;
      }

      // Populate cache for all games at once
      gamesList.forEach((g: any) => {
        if (g.card_back_url) {
          if (g.id) cardBackCache.set(`id:${g.id}`, g.card_back_url);
          if (g.name) cardBackCache.set(`game:${g.name.toLowerCase().trim()}`, g.card_back_url);
          if (g.slug) cardBackCache.set(`game:${g.slug.toLowerCase().trim()}`, g.card_back_url);
        }
      });

      if (!isMounted) return;

      let matchedGame: any = null;
      if (gameId) {
        matchedGame = gamesList.find((g: any) => String(g.id) === String(gameId));
      }
      if (!matchedGame && game) {
        const gameStr = String(game).toLowerCase().trim();
        matchedGame = gamesList.find((g: any) =>
          g.name?.toLowerCase() === gameStr ||
          g.slug?.toLowerCase() === gameStr ||
          gameStr.includes(g.name?.toLowerCase()) ||
          g.name?.toLowerCase().includes(gameStr)
        );
      }

      if (matchedGame?.card_back_url) {
        setResolvedBack(matchedGame.card_back_url);
      } else {
        setResolvedBack(DEFAULT_CARD_BACK);
      }
    }).catch(() => {
      if (isMounted) setResolvedBack(DEFAULT_CARD_BACK);
    });

    return () => {
      isMounted = false;
    };
  }, [game, gameId, cardBackUrl, cacheKey]);

  // Handle source changes and cached image checks
  useEffect(() => {
    setHasFrontError(false);

    if (!normalizedSrc) {
      setIsFrontLoading(false);
      return;
    }

    // Check if element is already loaded in browser cache
    if (frontImgRef.current) {
      if (frontImgRef.current.complete && frontImgRef.current.naturalWidth > 0) {
        setIsFrontLoading(false);
      } else {
        setIsFrontLoading(true);
      }
    } else {
      setIsFrontLoading(true);
    }
  }, [normalizedSrc]);

  const handleFrontLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth > 0) {
      setIsFrontLoading(false);
      setHasFrontError(false);
    } else {
      setIsFrontLoading(true);
    }
  };

  const handleFrontError = () => {
    setIsFrontLoading(false);
    setHasFrontError(true);
  };

  const showBackOnly = hasFrontError || !normalizedSrc;
  const imgFitClass = fitMode === 'contain' ? 'object-contain' : 'object-cover';

  const imageElement = (
    <div className={`relative overflow-hidden w-full h-full bg-slate-950 flex items-center justify-center ${className}`}>
      {/* 1. Card Back / Loading Placeholder */}
      {(isFrontLoading || showBackOnly) && (
        <img
          src={resolvedBack}
          alt="Card Back"
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 w-full h-full ${imgFitClass} transition-opacity duration-200 pointer-events-none ${
            showBackOnly ? 'opacity-100' : 'opacity-40'
          }`}
          referrerPolicy="no-referrer"
        />
      )}

      {/* 2. Main Card Front Image with lazy loading and async decoding */}
      {!showBackOnly && (
        <img
          ref={frontImgRef}
          src={normalizedSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={handleFrontLoad}
          onError={handleFrontError}
          className={`w-full h-full ${imgFitClass} transition-opacity duration-200 ${
            isFrontLoading ? 'opacity-0 absolute inset-0' : 'opacity-100 relative'
          }`}
          referrerPolicy="no-referrer"
        />
      )}
    </div>
  );

  if (layoutId) {
    return (
      <motion.div layoutId={layoutId} className="w-full h-full flex items-center justify-center">
        {imageElement}
      </motion.div>
    );
  }

  return imageElement;
};

export const CardImage = React.memo(CardImageComponent);
